import DeleteIcon from "@mui/icons-material/Delete";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import LocalPrintshopRoundedIcon from "@mui/icons-material/LocalPrintshopRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Backdrop,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Popper,
  Table,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { t } from "i18next";
import print from "print-js";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CoreDialog from "../../../../../app/core/components/CoreDialog/CoreDialog";
import CoreModal from "../../../../../app/core/components/CoreModal/CoreModal";
import CorePagination from "../../../../../app/core/components/CorePagination/CorePagination";
import CorePreviewDoc from "../../../../../app/core/components/CorePreviewDoc/CorePreviewDoc";
import CoreSpinner from "../../../../../app/core/components/CoreSpinner/CoreSpinner";
import CoreStatus from "../../../../../app/core/components/CoreStatus/CoreStatus";
import { GESTIONE_DOCUMENTI_TABLE_HEADER_USER } from "../../../../../utils/constants/tables";
import { formattaDataOraIta } from "../../../../../utils/functions/date-utils";
import {
  useDeleteDocumentMutation,
  useGetDocumentByIdQuery,
} from "../../../../api/user/gestisciDocumentiApi";
import { actions } from "../../gestisciDocumentiSlice";
import EditNameDoc from "../EditNameDoc";

const GestioneDocumentiTable = ({
  data,
  page,
  setPage,
  orderDirection,
  setOrderDirection,
  valueToOrderBy,
  setValueToOrderBy,
  isLoading,
  totalPages,
}) => {
  const appConfig = useSelector((state) => state.app.appConfig);

  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [showConfirmUserDialog, setShowConfirmUserDialog] = useState(false);
  const [notesEl, setNotesEl] = useState();
  const [profile, setProfile] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSingleDocumentModalOpen, setIsSingleDocumentModalOpen] =
    useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docDelete, setDocDelete] = useState();

  const [deleteDocument, { isLoading: isDeletingDocument }] =
    useDeleteDocumentMutation();

  const { data: singleDoc, isLoading: loadingDoc } = useGetDocumentByIdQuery(
    selectedDoc,
    {
      skip: !selectedDoc,
    }
  );

  const dispatch = useDispatch();

  //SELECTION
  const docManagementState = useSelector((state) => state.docManagementSlice);
  const colorPrimary = useSelector((state) => state.app.primaryColor);

  const handleSelectAllClick = () => {
    const selectedIds = docManagementState.selectedDocsIds;

    if (selectedIds.length === data?.items?.length) {
      dispatch(actions.setSelectedDocsIds([]));
    } else {
      let newSelected = [];
      data?.items.forEach((e) => newSelected.push(e.id));
      dispatch(actions.setSelectedDocsIds(newSelected));
    }
  };

  const handleSelectSingleDoc = (id) => {
    const selectedIndex = docManagementState.selectedDocsIds.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = [...docManagementState.selectedDocsIds, id];
    } else {
      newSelected = [
        ...docManagementState.selectedDocsIds.slice(0, selectedIndex),
        ...docManagementState.selectedDocsIds.slice(selectedIndex + 1),
      ];
    }
    dispatch(actions.setSelectedDocsIds(newSelected));
  };

  const isSelected = (id) =>
    docManagementState.selectedDocsIds.indexOf(id) !== -1;

  // Function to close the modal
  const closeModal = () => {
    setIsSingleDocumentModalOpen(false);
  };

  const closeModalEdit = () => {
    setOpenModalEdit(false);
  };

  // OPEN SINGLE DOC
  const handleOpenSingleDoc = (doc) => {
    setSelectedDoc(doc);
    setIsSingleDocumentModalOpen(true);
  };

  const handleOpenEditModal = (doc) => {
    setSelectedDoc(doc);
    setOpenModalEdit(true);
  };

  const handleOpenDialog = (el) => {
    setDocDelete(el);
    setShowConfirmUserDialog(true);
  };

  const handleCloseDialod = () => {
    setShowConfirmUserDialog(false);
    setDocDelete(null);
  };

  const handleConfermDialog = () => {
    const id = toast.loading(t("Deleting..."), {
      type: "info",
    });
    deleteDocument(docDelete.id)
      .then((res) => {
        if (res.data.Result !== 1) {
          toast.update(id, {
            render: res.data.Exception || t("DocNotDeleted"),
            type: "error",
            autoClose: 1500,
            isLoading: false,
          });
          handleCloseDialod();
          return;
        }

        handleCloseDialod();
        toast.update(id, {
          render: t("DocDeleted"),
          type: "success",
          autoClose: 1500,
          isLoading: false,
        });
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  // SORT LOGIC
  const handleRequestSort = (property) => {
    const isAscending = valueToOrderBy === property && orderDirection === "asc";
    setValueToOrderBy(property);
    setOrderDirection(isAscending ? "desc" : "asc");
  };

  const handlePopoverOpen = (event, note, profile) => {
    setAnchorEl(event.currentTarget);
    setNotesEl(note);
    setProfile(profile);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  function isIndeterminate(numberSelected, numberOfData) {
    return numberSelected > 0 && numberSelected < numberOfData;
  }

  if (loadingDoc) {
    return <CoreSpinner />;
  }

  return (
    <div className="w-full p-4">
      <TableContainer sx={{ position: "relative" }}>
        <Backdrop
          className="bg-black bg-opacity-30"
          sx={{ position: "absolute", zIndex: 1 }}
          open={isLoading}
        >
          <CircularProgress style={{ color: colorPrimary }} />
        </Backdrop>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size="medium"
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isIndeterminate(
                    docManagementState.selectedDocsIds?.length,
                    data?.items?.length
                  )}
                  checked={
                    docManagementState.selectedDocsIds?.length ===
                    data?.items?.length
                  }
                  onChange={handleSelectAllClick}
                  inputProps={{ "aria-label": "select all documents" }}
                />
              </TableCell>
              {GESTIONE_DOCUMENTI_TABLE_HEADER_USER()
                .filter(
                  (col) => !["date", "amount", "category"].includes(col.id)
                )
                .map((column) => (
                  <TableCell
                    key={column.id}
                    align="left"
                    style={{ minWidth: column.minWidth }}
                  >
                    <TableSortLabel
                      disabled={
                        column.id === "status" ||
                        column.id === "action" ||
                        column.id === "category"
                      }
                      active={valueToOrderBy === column.id}
                      direction={orderDirection}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.items?.map((doc) => {
              const isItemSelected = isSelected(doc.id);
              return (
                <TableRow
                  className="hover:shadow-md"
                  key={doc.Id}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  selected={isItemSelected}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isItemSelected}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => handleSelectSingleDoc(doc.id)}
                      inputProps={{ "aria-labelledby": doc.id }}
                    />
                  </TableCell>
                  <TableCell align="left"> {doc.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-col justify-center">
                      <CoreStatus status={doc.status} />
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    {formattaDataOraIta(doc.createdAt)}
                  </TableCell>

                  <TableCell>
                    <div
                      aria-owns={open ? "mouse-over-popover" : undefined}
                      aria-haspopup="true"
                      onMouseEnter={(e) =>
                        handlePopoverOpen(e, doc.notes, t("User"))
                      }
                      onMouseLeave={handlePopoverClose}
                    >
                      {doc?.notes?.length > 30
                        ? doc.notes.substring(0, 27) + "..."
                        : doc.notes}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div
                      aria-owns={open ? "mouse-over-popover" : undefined}
                      aria-haspopup="true"
                      onMouseEnter={(e) =>
                        handlePopoverOpen(e, doc.operatorNotes, t("Operator"))
                      }
                      onMouseLeave={handlePopoverClose}
                    >
                      {doc?.operatorNotes?.length > 30
                        ? doc.notes.substring(0, 27) + "..."
                        : doc.operatorNotes}
                    </div>
                  </TableCell>

                  <TableCell className="items-center" align="left">
                    <Tooltip title={t("Visualize")}>
                      <IconButton
                        onClick={() => handleOpenSingleDoc(doc.id)}
                        className="w-10 h-10"
                      >
                        <SearchRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifica">
                      <IconButton
                        onClick={() => handleOpenEditModal(doc.id)}
                        className="w-10 h-10"
                      >
                        <EditRoundedIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t("Delete")}>
                      <IconButton
                        disabled={
                          appConfig?.modules?.documents?.deleteBlock &&
                          doc.status === 5
                        }
                        onClick={() => handleOpenDialog(doc)}
                        className="w-10 h-10"
                      >
                        <DeleteIcon
                          className={
                            appConfig?.modules?.documents?.deleteBlock &&
                            doc.status === 5
                              ? ""
                              : "text-red-600"
                          }
                        />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t("Print")}>
                      <IconButton
                        onClick={() => {
                          print({
                            printable: `${process.env.REACT_APP_API_URL}${doc.files[0].url}`,
                            type: `${
                              ["jpg", "jpeg", "png"].includes(
                                doc.files[0].extension
                              )
                                ? "image"
                                : doc.files[0].extension
                            }`,
                            header: `AppStudio - ${doc.name}`,
                          });
                        }}
                        className="w-10 h-10 "
                      >
                        <LocalPrintshopRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            <Popper
              id="mouse-over-popover"
              sx={{
                pointerEvents: "none",
              }}
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              disableRestoreFocus
              disableScrollLock
            >
              <div
                style={{
                  width: "350px",
                  backgroundColor: "white",
                  borderRadius: 20,
                }}
                className="shadow-lg p-4"
              >
                <h3 className="p-4 m-0 text-center">
                  {t("Note") + " " + profile}
                </h3>
                <Divider />
                <p className="p-4">{notesEl}</p>
              </div>
            </Popper>
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <div className="w-full flex justify-center">
        <CorePagination page={page} setPage={setPage} totalPages={totalPages} />
      </div>
      <CoreModal
        width={"60%"}
        height={"95%"}
        onClose={closeModal}
        state={isSingleDocumentModalOpen}
        component={<CorePreviewDoc singleDoc={singleDoc?.Data} />}
      />
      <CoreModal
        width={500}
        height={450}
        onClose={closeModalEdit}
        state={openModalEdit}
        component={
          <EditNameDoc document={singleDoc?.Data} close={closeModalEdit} />
        }
      />
      <CoreDialog
        disabledButton={isDeletingDocument}
        open={showConfirmUserDialog}
        title={t("Delete") + " " + t("document")}
        content={`${t("DocDeletingConfirm")} ${docDelete?.name}`}
        negativeButton={t("No")}
        positiveButton={isDeletingDocument ? t("Wait") : t("Yes")}
        handlePositive={handleConfermDialog}
        closeDialog={handleCloseDialod}
      />
    </div>
  );
};

export default GestioneDocumentiTable;
